import { useCallback, useEffect, useRef, useState } from 'react';
import { useBeforeUnload, useNavigate } from 'react-router-dom';

type PendingExit = { type: 'back' } | { type: 'link'; href: string };

interface UseQuizExitGuardOptions {
  when: boolean;
  /** Called when the user confirms leave; throw to cancel navigation. */
  onConfirmLeave?: () => void | Promise<void>;
}

export function useQuizExitGuard({ when, onConfirmLeave }: UseQuizExitGuardOptions) {
  const navigate = useNavigate();
  const allowLeaveRef = useRef(false);
  const [pendingExit, setPendingExit] = useState<PendingExit | null>(null);

  const showDialog = pendingExit !== null;

  useBeforeUnload(
    useCallback(
      (event) => {
        if (!when || allowLeaveRef.current) return;
        event.preventDefault();
        event.returnValue = '';
      },
      [when],
    ),
  );

  useEffect(() => {
    if (!when) return;

    allowLeaveRef.current = false;
    window.history.pushState({ quizExitGuard: true }, '');

    const handlePopState = () => {
      if (allowLeaveRef.current) return;
      window.history.pushState({ quizExitGuard: true }, '');
      setPendingExit({ type: 'back' });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [when]);

  useEffect(() => {
    if (!when) return;

    const handleClick = (event: MouseEvent) => {
      if (allowLeaveRef.current) return;

      const target = event.target as Element | null;
      const anchor = target?.closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:') || href.startsWith('#')) return;
      if (href === '/quiz' || href.startsWith('/quiz?')) return;

      event.preventDefault();
      event.stopPropagation();
      setPendingExit({ type: 'link', href });
    };

    document.addEventListener('click', handleClick, true);
    return () => document.removeEventListener('click', handleClick, true);
  }, [when]);

  const stayOnQuiz = useCallback(() => {
    setPendingExit(null);
  }, []);

  const leaveQuiz = useCallback(async () => {
    const exit = pendingExit;
    if (!exit) return;

    try {
      await onConfirmLeave?.();
    } catch {
      return;
    }

    allowLeaveRef.current = true;
    setPendingExit(null);

    if (exit.type === 'back') {
      window.history.go(-2);
      return;
    }

    navigate(exit.href);
  }, [navigate, onConfirmLeave, pendingExit]);

  return { showDialog, stayOnQuiz, leaveQuiz };
}
